import { Brain, Loader2, PenLine } from "lucide-react";
import { Button, cn } from "@eduviaos/ui-kit";
import { SectionCard } from "../Section";
import { Ziggy } from "../Ziggy";
import type { Pathway } from "../../lib/learning/types";
import type { ZiggyAge } from "../../lib/ziggy";
import type { PortalStrings } from "../../lib/i18n/usePortalLocale";

type PromoStrings = Pick<
  PortalStrings,
  | "topicsDuePromo"
  | "topicsDuePromoHint"
  | "findYourGaps"
  | "findYourGapsHint"
  | "nothingDue"
  | "nothingDueHint"
  | "startReviewing"
  | "buildingQuizButton"
  | "takeTheQuiz"
  | "newQuiz"
  | "pleasurePromoTitle"
  | "pleasurePromoHint"
  | "logReading"
>;

export function QuizPromoCard({
  ziggyAge = "grown",
  target,
  dueNow,
  reviewedToday,
  readsLoggedToday,
  pendingDiagnostic,
  pleasureMode = false,
  loading,
  quizBusy,
  strings,
  onReview,
  onTakeQuiz,
  error,
}: {
  ziggyAge?: ZiggyAge;
  target: Pathway | undefined;
  dueNow: number;
  reviewedToday: number;
  readsLoggedToday: number;
  pendingDiagnostic: boolean;
  /** Read-for-pleasure: reading log only — no quiz or spaced repetition. */
  pleasureMode?: boolean;
  loading: boolean;
  quizBusy: boolean;
  strings: PromoStrings;
  onReview: () => void;
  onTakeQuiz: () => void;
  error: string | null;
}) {
  const copy = loading ? null : pleasureMode ? (
    <>
      <p className="text-sm font-semibold leading-snug">{strings.pleasurePromoTitle}</p>
      <p className="text-muted-foreground mt-1 text-xs leading-relaxed">
        {strings.pleasurePromoHint(target?.title ?? "this plan")}
      </p>
    </>
  ) : dueNow > 0 ? (
    <>
      <p className="text-sm font-semibold leading-snug">{strings.topicsDuePromo(dueNow)}</p>
      <p className="text-muted-foreground mt-1 text-xs leading-relaxed">
        {strings.topicsDuePromoHint(reviewedToday, readsLoggedToday)}
      </p>
    </>
  ) : pendingDiagnostic ? (
    <>
      <p className="text-sm font-semibold leading-snug">{strings.findYourGaps}</p>
      <p className="text-muted-foreground mt-1 text-xs leading-relaxed">
        {strings.findYourGapsHint(target?.title ?? "your plan")}
      </p>
    </>
  ) : (
    <>
      <p className="text-sm font-semibold leading-snug">{strings.nothingDue}</p>
      <p className="text-muted-foreground mt-1 text-xs leading-relaxed">
        {strings.nothingDueHint(reviewedToday, readsLoggedToday)}
      </p>
    </>
  );

  const action =
    target &&
    (pleasureMode ? (
      <Button
        className="h-auto min-h-9 rounded-full px-5 py-2.5 text-xs sm:min-h-10 sm:min-w-[9.5rem] sm:px-6 sm:py-3 sm:text-sm"
        onClick={onTakeQuiz}
      >
        <PenLine className="mr-1.5 size-4 shrink-0" />
        {strings.logReading}
      </Button>
    ) : dueNow > 0 ? (
      <Button
        className="h-auto min-h-9 rounded-full px-5 py-2.5 text-xs sm:min-h-10 sm:min-w-[9.5rem] sm:px-6 sm:py-3 sm:text-sm"
        onClick={onReview}
      >
        {strings.startReviewing}
      </Button>
    ) : (
      <Button
        className="h-auto min-h-9 rounded-full px-5 py-2.5 text-[11px] leading-snug sm:min-h-10 sm:min-w-[9.5rem] sm:px-6 sm:py-3 sm:text-sm"
        disabled={quizBusy}
        onClick={onTakeQuiz}
      >
        {quizBusy ? (
          <>
            <Loader2 className="mr-1.5 size-4 animate-spin" /> {strings.buildingQuizButton}
          </>
        ) : (
          <>
            <Brain className="mr-1.5 size-4 shrink-0" />
            {pendingDiagnostic ? strings.takeTheQuiz : strings.newQuiz}
          </>
        )}
      </Button>
    ));

  return (
    <SectionCard className="overflow-hidden p-0 shadow-[var(--shadow-card)]">
      <div className="flex flex-col gap-3 p-4 sm:grid sm:grid-cols-[auto_minmax(0,1fr)_auto] sm:items-center sm:gap-x-3.5 sm:gap-y-0 sm:p-5">
        <div className="flex min-w-0 items-start gap-3 sm:contents">
          <Ziggy
            pose={pleasureMode ? "reading" : "standing"}
            age={ziggyAge}
            size={112}
            glow={false}
            float={false}
            alt=""
            className="shrink-0"
          />

          <div className="min-w-0 flex-1 pt-0.5 sm:pr-1 sm:pt-0">
            {loading ? (
              <div className="space-y-2">
                <div className="bg-muted/60 h-4 w-36 max-w-full animate-pulse rounded" />
                <div className="bg-muted/60 h-3 w-48 max-w-full animate-pulse rounded" />
              </div>
            ) : (
              copy
            )}
          </div>
        </div>

        {action && (
          <div className="flex shrink-0 justify-end sm:justify-self-end sm:pl-0.5">{action}</div>
        )}
      </div>

      {error && (
        <p
          role="alert"
          className={cn(
            "bg-destructive/10 text-destructive border-border/50 border-t px-4 py-2.5 text-center text-xs font-medium",
          )}
        >
          {error}
        </p>
      )}
    </SectionCard>
  );
}
